'use client';

import { useEffect, useRef, useState, type PropsWithChildren } from 'react';
import { usePathname } from 'next/navigation';
import Link from '@eliadmin/core/src/components/link';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { CategoriesList } from '../shared/categories-list';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { IoIosArrowDown } from 'react-icons/io';

export type CategoryMenuProps = {
  className?: string;
};

export function CategoryMenu(props: PropsWithChildren<CategoryMenuProps>) {
  return (
    <ul className={cn('flex flex-row items-center gap-2', 'ea-category-menu', props.className)}>
      {props.children}
    </ul>
  );
}

export type CategoryMenuItemProps = {
  title: string;
  href?: string;
  onClick?: () => void;
  order?: number;
  className?: {
    root?: string;
    link?: string;
  };
  categories: CategoriesEntity[];
};

export function CategoryMenuItem(props: CategoryMenuItemProps) {
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  const LinkComp = props.href ? (isRelativeHref(getSafeHref(props.href)) ? Link : 'a') : 'span';
  const categoriesSlugs = props.categories.map(category => category.slug);
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const pathname = usePathname();

  const isActive =
    pathname === getSafeHref(props?.href ?? '') ||
    (locale === 'en' && pathname === '/en' && getSafeHref(props?.href ?? '') === '/') ||
    (locale === 'en' && pathname === `/en/${props.href}`) ||
    (props.title === t('text1244') && categoriesSlugs.some(slug => pathname === `/${slug}`));

  const handleMouseEnter = () => {
    setIsMouseOver(true);
    setDropdownVisible(true);
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
  };

  const handleMouseLeave = () => {
    setIsMouseOver(false);
    hoverTimeout.current = setTimeout(() => {
      if (!isMouseOver) {
        setDropdownVisible(false);
      }
    }, 300);
  };

  useEffect(() => {
    const handleMouseOver = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        (dropdownRef.current.contains(event.target as Node) || event.target === dropdownRef.current)
      ) {
        setIsMouseOver(true);
        setDropdownVisible(true);
        if (hoverTimeout.current) {
          clearTimeout(hoverTimeout.current);
        }
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        event.target !== dropdownRef.current
      ) {
        handleMouseLeave();
      }
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [isMouseOver]);

  useEffect(() => {
    setDropdownVisible(false);
  }, [pathname]);

  return (
    <li
      style={{ order: props.order }}
      className={cn('flex flex-row', 'ea-category-menu-item', props.className?.root)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <LinkComp {...(LinkComp === Link ? { prefetchOnHover: true } : {})}
        href={props.href ? getSafeHref(props.href) : ((undefined as any) ?? '#')}
        className={cn(
          'flex items-center gap-2 rounded-none px-3 py-2 font-heading text-sm uppercase xl:text-base',
          'ea-category-menu-item__link',
          isActive
            ? 'ea-category-menu-item__link--active font-bold text-secondary'
            : 'font-normal text-foreground',
          props.className?.link,
          props.title === t('text1244') && '!hover:text-secondary !hover:font-bold',
        )}
        onClick={props.onClick}
      >
        {props.title}{' '}
        {props.title === t('text1244') && <IoIosArrowDown className="text-secondary" />}
      </LinkComp>
      {props.title === t('text1244') && isDropdownVisible && (
        <>
          <div className="absolute left-0 top-[93px] z-[10] h-screen w-full bg-black/50" />
          <div
            ref={dropdownRef}
            className="absolute left-0 top-full z-[500] flex w-[99.1vw] flex-col items-center justify-center bg-white p-12"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <CategoriesList locale={locale} />
          </div>
        </>
      )}
    </li>
  );
}
